import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface ShiftRangeIndexes {
  start: number;
  end: number;
}

@Component({
  selector: 'app-bulk-actions',
  templateUrl: './bulk-actions.component.html',
  styleUrls: ['./bulk-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BulkActionsComponent<T, K extends keyof any = string> {
  private readonly isShiftPressed$ = new BehaviorSubject<boolean>(false);
  private shiftRangeIndexes: ShiftRangeIndexes = { start: null, end: null };
  private _items: T[];

  @Input({ required: true })
  set items(items: T[]) {
    this._items = this.filteringCallback ? items.filter((listItem) => this.filteringCallback(listItem)) : items;
    this.handleItemsUpdate();
  }

  @Input({ required: true })
  itemIdSelector: (item: T) => K;

  @Input()
  filteringCallback: (item: T) => boolean;

  @Output()
  selectedItemsMap = new Map<K, T>();

  @HostBinding('class.invisible')
  private get areBulkActionsHidden(): boolean {
    return !this.selectedCount;
  }

  private get areShiftIndexesSet(): boolean {
    return this.shiftRangeIndexes.start !== null && this.shiftRangeIndexes.end !== null;
  }

  private get areShiftIndexesNotSet(): boolean {
    return this.shiftRangeIndexes.start === null && this.shiftRangeIndexes.end === null;
  }

  private get startIndex(): number {
    return this.shiftRangeIndexes.start < this.shiftRangeIndexes.end
      ? this.shiftRangeIndexes.start
      : this.shiftRangeIndexes.end;
  }

  private get endIndex(): number {
    return (
      (this.shiftRangeIndexes.start < this.shiftRangeIndexes.end
        ? this.shiftRangeIndexes.end
        : this.shiftRangeIndexes.start) + 1
    );
  }

  get selectedCount(): number {
    return this.selectedItemsMap.size;
  }

  get totalCount(): number {
    return this._items.length;
  }

  get isAllSelected(): boolean {
    return this.selectedCount === this.totalCount;
  }

  get selectedItems(): K[] {
    return Array.from(this.selectedItemsMap.keys());
  }

  get selectedCountTranslationKey(): string {
    if (this.isAllSelected) {
      return this.buildSelectedCountTranslationKey('all');
    }

    if (this.selectedCount === 1) {
      return this.buildSelectedCountTranslationKey('singular');
    }

    return this.buildSelectedCountTranslationKey('plural');
  }

  constructor(private cd: ChangeDetectorRef) {}

  unSelectAll(): void {
    this.selectedItemsMap.clear();
    this.shiftRangeIndexes = { start: null, end: null };
    this.cd.detectChanges();
  }

  selectAll(): void {
    for (const item of this._items) {
      this.selectedItemsMap.set(this.itemIdSelector(item), item);
    }

    this.cd.detectChanges();
  }

  isItemSelected(item: T): boolean {
    const itemId = this.itemIdSelector(item);

    return this.selectedItemsMap.has(itemId);
  }

  toggleItemSelection(item: T): void {
    const itemId = this.itemIdSelector(item);

    if (!this.isItemSelected(item) && this.areShiftIndexesNotSet) {
      this.shiftRangeIndexes.start = this.findItemIndex(itemId);
    }

    if (
      !this.isItemSelected(item) &&
      this.isShiftPressed$.value &&
      this.shiftRangeIndexes.start !== null &&
      this.shiftRangeIndexes.start !== this.findItemIndex(itemId)
    ) {
      this.shiftRangeIndexes.end = this.findItemIndex(itemId);

      const itemsToSelect = this._items.slice(this.startIndex, this.endIndex);

      itemsToSelect.forEach((itemToSelect) =>
        this.selectedItemsMap.set(this.itemIdSelector(itemToSelect), itemToSelect),
      );
    } else {
      if (this.isItemSelected(item)) {
        this.selectedItemsMap.delete(itemId);
      } else {
        if (this.areShiftIndexesSet) {
          this.unSelectAll();
          this.shiftRangeIndexes = { start: this.findItemIndex(itemId), end: null };
        }

        this.selectedItemsMap.set(itemId, item);
      }
    }

    this.cd.detectChanges();
  }

  buildTranslationKey(relativeKey: string): string {
    return `bulkActions.${relativeKey}`;
  }

  buildSelectedCountTranslationKey(relativeKey: string): string {
    return `${this.buildTranslationKey('selectedCount')}.${relativeKey}`;
  }

  private findItemIndex(itemId: string | number | symbol): number {
    return this._items.findIndex((listItem) => this.itemIdSelector(listItem) === itemId);
  }

  private handleItemsUpdate(): void {
    const itemIds = new Set(this._items.map((item: T) => this.itemIdSelector(item)));

    [...this.selectedItemsMap.keys()].forEach((key: K) => {
      if (!itemIds.has(key)) {
        this.selectedItemsMap.delete(key);
      }
    });

    this.cd.detectChanges();
  }

  @HostListener('document:keydown.escape', ['$event'])
  private onKeydownHandler(): void {
    this.unSelectAll();
  }

  @HostListener('document:keydown.shift', ['$event'])
  private onShiftKeydownHandler(): void {
    this.isShiftPressed$.next(true);
  }

  @HostListener('document:keyup.shift', ['$event'])
  private onShiftKeyupHandler(): void {
    this.isShiftPressed$.next(false);
  }
}
